import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useToast } from '@/hooks/use-toast';
import { 
  connectToPhantomInApp, 
  disconnectFromPhantomInApp, 
  forceClosePhantomWindow,
  isPhantomInAppBrowser,
  isPhantomMobileApp,
  isUserRejectionError 
} from '../utils/phantomUtils';
import { useWalletState } from './useWalletState';

/**
 * Hook that provides wallet connection and disconnection handlers
 */
export const useWalletHandlers = () => {
  const { connect, disconnect, connecting } = useWallet();
  const { visible, setVisible } = useWalletModal();
  const { toast } = useToast();
  
  const {
    wallet,
    wallets,
    connected,
    isSelectingWallet,
    setIsSelectingWallet,
    connectionAttempts,
    setConnectionAttempts,
    select
  } = useWalletState();

  const handleConnect = async (e?: React.MouseEvent) => {
    try {
      // Prevent default behavior to avoid page reload
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Check if we're in Phantom's in-app browser
      if (isPhantomInAppBrowser() && window?.solana) {
        console.log("Detected Phantom in-app browser, using direct connection...");
        try {
          // Use direct Phantom connection
          const connected = await connectToPhantomInApp();
          
          // Wait briefly for the connection to register
          setTimeout(() => {
            if (window?.solana?.isConnected) {
              toast({
                title: "Wallet Connected",
                description: "Your wallet is now connected to PopCoin!",
                duration: 2000, // Auto-dismiss after 2 seconds
              });
            }
          }, 300);
          
          return;
        } catch (phantomError: any) {
          // If direct connection fails, check if it was a user rejection
          if (isUserRejectionError(phantomError)) {
            // Use our custom Phantom toast with auto-dismiss
            toast({
              variant: "phantom",
              title: "Wallet Action Canceled",
              description: "Connection request cancelled",
              duration: 2500, // Auto-dismiss after 2.5 seconds
            });
            return;
          }
          
          // For other Phantom-specific errors, fall through to regular adapter
          console.log("Direct Phantom connection failed, falling back to adapter:", phantomError);
        }
      }

      if (connecting || isSelectingWallet) {
        // Prevent multiple connection attempts
        console.log("Already attempting connection, please wait...");
        return;
      }
      
      if (!wallet && wallets.length > 0) {
        // If no wallet is selected but Phantom is available, select it
        const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom');
        if (phantomWallet) {
          // First select the wallet
          setIsSelectingWallet(true);
          console.log("Selecting Phantom wallet...");
          select(phantomWallet.adapter.name);
          
          // Reduced delay to ensure wallet selection is processed
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Now try to connect
          console.log("Attempting to connect to selected wallet...");
          await connect();
          setIsSelectingWallet(false);
          
          toast({
            title: "Wallet Connected",
            description: "Your wallet is now connected to PopCoin!",
            duration: 2000, // Auto-dismiss after 2 seconds
          });
          return;
        }
      }
      
      // If wallet is already selected or no wallet was found
      if (wallet) {
        setConnectionAttempts(prev => prev + 1);
        console.log(`Connection attempt ${connectionAttempts + 1}`);
        
        await connect();
        toast({
          title: "Wallet Connected",
          description: "Your wallet is now connected to PopCoin!",
          duration: 2000, // Auto-dismiss after 2 seconds
        });
      } else {
        // If no wallets are available, show the wallet modal
        setVisible(true);
      }
    } catch (error: any) {
      setIsSelectingWallet(false);
      console.error("Wallet connection error:", error);
      
      // Check for user rejection specifically
      if (isUserRejectionError(error)) {
        toast({
          variant: "phantom",
          title: "Wallet Action Canceled",
          description: "Connection request cancelled",
          duration: 2500, // Auto-dismiss after 2.5 seconds
        });
        return;
      }
      
      // More detailed error messaging
      if (error.name === "WalletNotFoundError") {
        toast({
          variant: "destructive",
          title: "Wallet Not Found",
          description: "Please install the Phantom wallet extension and refresh the page.",
          duration: 4000, // Give user more time to read error messages
        });
      } else if (error.name === "WalletNotReadyError" || error.name === "WalletNotSelectedError") {
        toast({
          variant: "destructive",
          title: "Wallet Not Ready",
          description: "Please ensure Phantom wallet is installed and try again.",
          duration: 4000,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Connection Error",
          description: error.message || "Failed to connect wallet. Please try again.",
          duration: 4000,
        });
      }
    }
  };

  const handleDisconnect = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      // Check if we're in Phantom mobile app
      const isPhantomMobile = isPhantomMobileApp();
      console.log(`Disconnecting wallet (Phantom Mobile: ${isPhantomMobile})`);
      
      // For Phantom in-app browser, we need special handling
      if (isPhantomInAppBrowser() && window?.solana?.isConnected) {
        console.log("Using direct Phantom disconnect for in-app browser...");
        
        try {
          // Try to disconnect directly via Phantom first
          const disconnected = await disconnectFromPhantomInApp();
          
          if (disconnected) {
            console.log("Direct Phantom disconnect successful");
            
            // Show toast after successful disconnect
            toast({
              title: "Wallet Disconnected",
              description: "Your wallet has been disconnected from PopCoin.",
              duration: 2000,
            });
            
            // For mobile Phantom, we need to try multiple approaches to close the window
            if (isPhantomMobile) {
              console.log("Starting sequence to close Phantom mobile window...");
              
              // First attempt immediately
              forceClosePhantomWindow();
              
              // Second attempt after a short delay
              setTimeout(() => {
                console.log("Second attempt to close Phantom window...");
                forceClosePhantomWindow();
              }, 800);
              
              // Third attempt after a longer delay
              setTimeout(() => {
                console.log("Final attempt to close Phantom window...");
                forceClosePhantomWindow();
                
                // Try deep linking as a last resort
                try {
                  console.log("Trying deep linking fallback...");
                  window.location.href = 'phantom://close';
                } catch (e) {
                  console.log("Deep linking fallback failed:", e);
                }
              }, 1500);
            }
            
            return;
          } else {
            console.log("Direct Phantom disconnect returned false, falling back to adapter...");
          }
        } catch (phantomError) {
          console.error("Error with direct Phantom disconnect:", phantomError);
          // Fall through to adapter disconnect if direct method fails
        }
      }

      // Standard adapter disconnect for non-mobile or fallback
      console.log("Using standard wallet adapter disconnect...");
      await disconnect();
      
      // Show toast after successful disconnect
      toast({
        title: "Wallet Disconnected",
        description: "Your wallet has been disconnected from PopCoin.",
        duration: 2000,
      });
      
      // For mobile Phantom, still try to force close as a fallback
      if (isPhantomMobileApp()) {
        console.log("Trying to force close window after standard disconnect...");
        setTimeout(() => {
          forceClosePhantomWindow();
        }, 300);
      }
    } catch (error: any) {
      console.error("Disconnect error:", error);
      toast({
        variant: "destructive",
        title: "Disconnection Error",
        description: error.message || "Failed to disconnect wallet",
        duration: 4000,
      });
      
      // Even if there's an error, try closing the window as a last resort for mobile
      if (isPhantomMobileApp()) {
        setTimeout(() => {
          console.log("Attempting force close after disconnect error...");
          forceClosePhantomWindow();
        }, 300);
      }
    }
  };

  return {
    handleConnect,
    handleDisconnect,
    openWalletModal: () => setVisible(true),
    connecting
  };
};
